import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook-fill.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram-fill.svg'
import classes from './adDetailsModal.module.scss'
import { ReactComponent as AmountIcon } from './svg/amount.svg'
import { ReactComponent as AudienceIcon } from './svg/audiance.svg'
import { ReactComponent as CategoryIcon } from './svg/category.svg'
import { ReactComponent as ImpressionsIcon } from './svg/immpresions.svg'
import { AdCardData } from '../AdCard'

const PLATFORM_ICONS: Record<
  string,
  { icon: FC<React.SVGProps<SVGSVGElement>>; label: string }
> = {
  facebook: { icon: FacebookIcon, label: 'Facebook' },
  instagram: { icon: InstagramIcon, label: 'Instagram' },
}

const ChevronIcon: FC<{ open: boolean }> = ({ open }) => (
  <svg
    fill="none"
    height="20"
    style={{
      flexShrink: 0,
      transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
      transition: 'transform 0.2s',
    }}
    viewBox="0 0 20 20"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 12.5L10 7.5L15 12.5"
      stroke="#25262D"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

interface AdDetailsModalProps {
  data: AdCardData
  isPolitical?: boolean
  onClose: () => void
  pageLogo: string
  pageName: string
  platforms: string[]
}

export const AdDetailsModal: FC<AdDetailsModalProps> = ({
  data,
  isPolitical,
  onClose,
  pageLogo,
  pageName,
  platforms,
}) => {
  const { links, photos, texts } = data.assets
  const hasAssets = links.length > 0 || photos.length > 0 || texts.length > 0

  const [assetsOpen, setAssetsOpen] = useState(false)
  const [advertiserOpen, setAdvertiserOpen] = useState(true)
  const [disclaimerOpen, setDisclaimerOpen] = useState(false)
  const [audienceOpen, setAudienceOpen] = useState(false)
  const [deliveryOpen, setDeliveryOpen] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  return createPortal(
    <div className={classes.overlay} onClick={handleOverlayClick}>
      <div className={classes.modal}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <img alt={pageName} className={classes.headerLogo} src={pageLogo} />
            <div className={classes.headerInfo}>
              <span className={classes.headerTitle}>Ad Details</span>
              <div className={classes.platformBadges}>
                {platforms.map((p) => {
                  const entry = PLATFORM_ICONS[p]
                  if (!entry) return null
                  const Icon = entry.icon
                  return (
                    <div className={classes.platformBadge} key={p}>
                      <Icon className={classes.platformIcon} />
                      <span className={classes.platformLabel}>
                        {entry.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <button className={classes.closeBtn} onClick={onClose} type="button">
            <svg
              fill="none"
              height="20"
              viewBox="0 0 20 20"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#25262D"
                strokeLinecap="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={classes.body}>
          {/* Left column */}
          <div className={classes.leftCol}>
            <div className={classes.adCard}>
              {/* Status badge */}
              {isPolitical ? (
                <div
                  className={
                    data.status === 'active'
                      ? classes.statusPillActive
                      : classes.statusPillInactive
                  }
                >
                  <svg
                    fill="none"
                    height="12"
                    viewBox="0 0 16 16"
                    width="12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {data.status === 'active' ? (
                      <>
                        <circle
                          cx="8"
                          cy="8"
                          fill="rgba(21,136,28,0.3)"
                          r="8"
                        />
                        <path
                          d="M4.5 8l2.5 2.5 4.5-5"
                          stroke="#15881C"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </>
                    ) : (
                      <>
                        <circle
                          cx="8"
                          cy="8"
                          fill="rgba(255,107,53,0.3)"
                          r="8"
                        />
                        <circle cx="8" cy="8" fill="#FF6B35" r="3" />
                      </>
                    )}
                  </svg>
                  <span>
                    {data.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ) : (
                <div className={classes.statusBadge}>
                  {data.status === 'active' ? (
                    <>
                      <svg
                        fill="none"
                        height="16"
                        viewBox="0 0 16 16"
                        width="16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          fill="rgba(21,136,28,0.15)"
                          r="8"
                        />
                        <path
                          d="M4.5 8l2.5 2.5 4.5-5"
                          stroke="#15881C"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                      <span className={classes.statusActive}>Active</span>
                    </>
                  ) : (
                    <>
                      <svg
                        fill="none"
                        height="16"
                        viewBox="0 0 16 16"
                        width="16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          fill="rgba(255,107,53,0.15)"
                          r="8"
                        />
                        <circle cx="8" cy="8" fill="#FF6B35" r="3" />
                      </svg>
                      <span className={classes.statusInactive}>Inactive</span>
                    </>
                  )}
                </div>
              )}

              <span className={classes.infoText}>
                Library ID: {data.libraryId}
              </span>
              <span className={classes.infoText}>
                Started running on {data.startedRunning}
              </span>
              <div className={classes.platformsRow}>
                <span className={classes.infoText}>Platforms</span>
                <div className={classes.platformIcons}>
                  {data.platforms.map((p) => {
                    const entry = PLATFORM_ICONS[p]
                    if (!entry) return null
                    const Icon = entry.icon
                    return (
                      <div className={classes.platformIconWrapper} key={p}>
                        <Icon />
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Political extra rows */}
              {isPolitical && (
                <>
                  <div className={classes.infoRow}>
                    <span className={classes.infoText}>Categories</span>
                    <CategoryIcon className={classes.politicalIcon} />
                  </div>
                  <div className={classes.infoRow}>
                    <AudienceIcon className={classes.politicalIcon} />
                    <span className={classes.infoText}>
                      Estimated audience size:{' '}
                      <strong className={classes.infoValueBold}>&gt;1M</strong>
                    </span>
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 24 24"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#25262D"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M12 8v4M12 16h.01"
                        stroke="#25262D"
                        strokeLinecap="round"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  <div className={classes.infoRow}>
                    <AmountIcon className={classes.politicalIcon} />
                    <span className={classes.infoText}>
                      Amount spent (USD):{' '}
                      <strong className={classes.infoValueBold}>
                        $400 - $499
                      </strong>
                    </span>
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 24 24"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#25262D"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M12 8v4M12 16h.01"
                        stroke="#25262D"
                        strokeLinecap="round"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  <div className={classes.infoRow}>
                    <ImpressionsIcon className={classes.politicalIcon} />
                    <span className={classes.infoText}>
                      Impressions:{' '}
                      <strong className={classes.infoValueBold}>
                        600K - 700K
                      </strong>
                    </span>
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 24 24"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#25262D"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M12 8v4M12 16h.01"
                        stroke="#25262D"
                        strokeLinecap="round"
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                </>
              )}

              {/* Preview */}
              <div className={classes.preview}>
                <div className={classes.previewHeader}>
                  <img
                    alt={pageName}
                    className={classes.previewLogo}
                    src={pageLogo}
                  />
                  <div className={classes.previewInfo}>
                    <span className={classes.previewName}>{pageName}</span>
                    <span className={classes.previewSponsored}>
                      {isPolitical
                        ? `Sponsored · Paid for by ${pageName}`
                        : 'Sponsored'}
                    </span>
                  </div>
                </div>
                <p className={classes.previewBody}>{data.body || ''}</p>
                {data.snapshotUrl && (
                  <a
                    className={classes.creativePreview}
                    href={data.snapshotUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <svg
                      fill="none"
                      height="24"
                      viewBox="0 0 24 24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        height="18"
                        rx="2"
                        stroke="#86949D"
                        strokeWidth="1.5"
                        width="18"
                        x="3"
                        y="3"
                      />
                      <circle
                        cx="8.5"
                        cy="8.5"
                        r="1.5"
                        stroke="#86949D"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M21 15l-5-5L5 21"
                        stroke="#86949D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                    <span className={classes.creativePreviewText}>
                      Creative preview on Facebook
                      <svg
                        fill="none"
                        height="14"
                        viewBox="0 0 24 24"
                        width="14"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 17L17 7M17 7H8M17 7V16"
                          stroke="#8080FF"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </span>
                  </a>
                )}
                {!isPolitical && (
                  <>
                    {data.headline && (
                      <div className={classes.previewFooter}>
                        <span className={classes.previewCtaText}>
                          {data.headline}
                        </span>
                      </div>
                    )}
                    <div className={classes.versionsFooter}>
                      <div className={classes.versionsLabel}>
                        <span className={classes.infoText}>
                          This ad has multiple versions
                        </span>
                        <svg
                          fill="none"
                          height="14"
                          viewBox="0 0 24 24"
                          width="14"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#25262D"
                            strokeOpacity="0.4"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M12 8v4M12 16h.01"
                            stroke="#25262D"
                            strokeLinecap="round"
                            strokeOpacity="0.4"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </div>
                      <span className={classes.versionCount}>1/2</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Additional assets — hidden for political */}
            {!isPolitical && hasAssets && (
              <div className={classes.assets}>
                <button
                  className={classes.assetsToggle}
                  onClick={() => setAssetsOpen((v) => !v)}
                  type="button"
                >
                  <span className={classes.assetsTitle}>
                    Additional assets from this ad
                  </span>
                  <svg
                    fill="none"
                    height="20"
                    style={{
                      transform: assetsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                    viewBox="0 0 20 20"
                    width="20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#25262D"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>

                {assetsOpen && (
                  <div className={classes.assetsContent}>
                    <p className={classes.assetsSubtitle}>
                      Additional content items from this ad
                    </p>

                    {links.length > 0 && (
                      <div className={classes.assetSection}>
                        <span className={classes.assetSectionTitle}>
                          <svg
                            fill="none"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                          </svg>{' '}
                          Links
                        </span>
                        <ul className={classes.assetList}>
                          {links.map((link) => (
                            <li key={link}>
                              <a
                                className={classes.assetLink}
                                href={link}
                                rel="noreferrer"
                                target="_blank"
                              >
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {texts.length > 0 && (
                      <div className={classes.assetSection}>
                        <span className={classes.assetSectionTitle}>
                          <svg
                            fill="none"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                          </svg>{' '}
                          Text
                        </span>
                        <ul className={classes.assetList}>
                          {texts.map((t) => (
                            <li className={classes.assetText} key={t}>
                              {t}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {photos.length > 0 && (
                      <div className={classes.assetSection}>
                        <span className={classes.assetSectionTitle}>
                          <svg
                            fill="none"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              height="18"
                              rx="2"
                              ry="2"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              width="18"
                              x="3"
                              y="3"
                            />
                            <circle
                              cx="8.5"
                              cy="8.5"
                              r="1.5"
                              stroke="#25262D"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M21 15l-5-5L5 21"
                              stroke="#25262D"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                            />
                          </svg>{' '}
                          Photos
                        </span>
                        <div className={classes.assetPhotos}>
                          {photos.map((src) => (
                            <img
                              alt="asset"
                              className={classes.assetPhoto}
                              key={src}
                              src={src}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className={classes.rightCol}>
            {/* Political-only sections */}
            {isPolitical && (
              <>
                <div className={classes.advertiserCard}>
                  <button
                    className={classes.advertiserHeader}
                    onClick={() => setDisclaimerOpen((v) => !v)}
                    type="button"
                  >
                    <span className={classes.advertiserTitle}>
                      About the disclaimer
                    </span>
                    <ChevronIcon open={disclaimerOpen} />
                  </button>
                  {disclaimerOpen && (
                    <div className={classes.disclaimerContent}>
                      <p className={classes.disclaimerDesc}>
                        When an advertiser categorizes their ad as being about
                        social issues, elections or politics, they are required
                        to disclose who paid for the ad.{' '}
                        <button className={classes.learnMoreLink} type="button">
                          Learn more
                        </button>
                      </p>

                      <div className={classes.disclaimerDivider} />

                      <div className={classes.disclaimerInfo}>
                        <span className={classes.disclaimerInfoTitle}>
                          Information from the advertiser
                        </span>
                        <span className={classes.disclaimerInfoSub}>
                          This information was provided to Meta.
                        </span>
                        <span className={classes.disclaimerInfoSub}>
                          Submitted May 1, 2023
                        </span>

                        {[
                          {
                            icon: (
                              <svg
                                fill="none"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                                <circle
                                  cx="12"
                                  cy="7"
                                  r="4"
                                  stroke="#25262D"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            ),
                            label: 'Disclaimer',
                            value: 'Georgian Dream',
                          },
                          {
                            icon: (
                              <svg
                                fill="none"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            ),
                            label: 'Phone number',
                            value: '+995322205073',
                          },
                          {
                            icon: (
                              <svg
                                fill="none"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M22 6l-10 7L2 6"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            ),
                            label: 'Email',
                            value: 'digital@gd.ge',
                          },
                          {
                            icon: (
                              <svg
                                fill="none"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="#25262D"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            ),
                            label: 'Website',
                            value: 'https://GD.ge/',
                          },
                          {
                            icon: (
                              <svg
                                fill="none"
                                height="16"
                                viewBox="0 0 24 24"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
                                  stroke="#25262D"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                />
                                <circle
                                  cx="12"
                                  cy="10"
                                  r="3"
                                  stroke="#25262D"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            ),
                            label: 'Address',
                            value: 'Tbilisi, Georgia',
                          },
                        ].map((item) => (
                          <div
                            className={classes.disclaimerRow}
                            key={item.label}
                          >
                            <div className={classes.disclaimerRowIcon}>
                              {item.icon}
                            </div>
                            <div className={classes.disclaimerRowText}>
                              <span className={classes.disclaimerRowLabel}>
                                {item.label}
                              </span>
                              <span className={classes.disclaimerRowValue}>
                                {item.value}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <p className={classes.disclaimerFooter}>
                        Visit the Help Center to learn about what we require
                        from advertisers to run ads about social issues,
                        elections or politics.{' '}
                        <button className={classes.learnMoreLink} type="button">
                          Learn more
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                <div className={classes.advertiserCard}>
                  <button
                    className={classes.advertiserHeader}
                    onClick={() => setAudienceOpen((v) => !v)}
                    type="button"
                  >
                    <span className={classes.advertiserTitle}>Ad audience</span>
                    <ChevronIcon open={audienceOpen} />
                  </button>
                  {audienceOpen && (
                    <div className={classes.audienceContent}>
                      <p className={classes.audienceDesc}>
                        Advertisers can target their ads to certain audiences
                        based on factors such as age, gender and location.
                      </p>
                      <div className={classes.audienceBox}>
                        <span className={classes.audienceBoxTitle}>
                          Estimated audience size
                        </span>
                        <span className={classes.audienceBoxValue}>&gt;1M</span>
                        <p className={classes.audienceBoxText}>
                          Estimated audience size is an estimate of how many{' '}
                          <button
                            className={classes.learnMoreLink}
                            type="button"
                          >
                            Accounts Center accounts
                          </button>{' '}
                          met an advertiser&apos;s targeting criteria when the
                          ad was created.
                        </p>
                        <p className={classes.audienceBoxText}>
                          This is not an estimate of how many people saw an ad
                          and is not designed to match population or census
                          data. This metric is{' '}
                          <button
                            className={classes.learnMoreLink}
                            type="button"
                          >
                            in development.
                          </button>
                        </p>
                        <button className={classes.learnMoreLink} type="button">
                          Learn more
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={classes.advertiserCard}>
                  <button
                    className={classes.advertiserHeader}
                    onClick={() => setDeliveryOpen((v) => !v)}
                    type="button"
                  >
                    <span className={classes.advertiserTitle}>Ad delivery</span>
                    <ChevronIcon open={deliveryOpen} />
                  </button>
                  {deliveryOpen && (
                    <div className={classes.deliveryContent}>
                      {/* Amount spent */}
                      <div className={classes.deliveryBox}>
                        <span className={classes.deliveryBoxLabel}>
                          Amount spent
                        </span>
                        <span className={classes.deliveryBoxValue}>
                          &lt;$100 (USD)
                        </span>
                        <p className={classes.deliveryBoxDesc}>
                          The estimated total amount of money spent on an ad
                          during its schedule.{' '}
                          <button
                            className={classes.learnMoreLink}
                            type="button"
                          >
                            Learn more
                          </button>
                        </p>
                      </div>

                      {/* Impressions */}
                      <div className={classes.deliveryBox}>
                        <span className={classes.deliveryBoxLabel}>
                          Impressions
                        </span>
                        <span className={classes.deliveryBoxValue}>
                          175K - 200K
                        </span>
                        <p className={classes.deliveryBoxDesc}>
                          The number of times an ad was on a screen, which may
                          include multiple views by the same people.{' '}
                          <button
                            className={classes.learnMoreLink}
                            type="button"
                          >
                            Learn more
                          </button>
                        </p>
                      </div>

                      {/* Age and gender */}
                      <div className={classes.deliverySection}>
                        <span className={classes.deliverySectionTitle}>
                          Age and gender
                        </span>
                        <div className={classes.barChartWrapper}>
                          <div className={classes.barChartYAxis}>
                            {['20%', '15%', '10%', '5%', '0%'].map((l) => (
                              <span className={classes.barChartYLabel} key={l}>
                                {l}
                              </span>
                            ))}
                          </div>
                          <div className={classes.barChart}>
                            <div className={classes.barChartBars}>
                              <div className={classes.barChartGridLines}>
                                {[0, 1, 2, 3, 4].map((i) => (
                                  <div
                                    className={classes.barChartGridLine}
                                    key={i}
                                  />
                                ))}
                              </div>
                              {[
                                { age: '18-24', men: 3, unknown: 5, women: 18 },
                                { age: '25-34', men: 8, unknown: 5, women: 3 },
                                { age: '35-44', men: 10, unknown: 2, women: 1 },
                                { age: '45-54', men: 1, unknown: 9, women: 9 },
                                { age: '55-64', men: 5, unknown: 17, women: 8 },
                                { age: '65+', men: 8, unknown: 10, women: 17 },
                              ].map((d) => (
                                <div className={classes.barGroup} key={d.age}>
                                  <div
                                    className={classes.bar}
                                    style={{
                                      background: 'rgba(128, 128, 247, 1)',
                                      height: `${(d.men / 20) * 150}px`,
                                    }}
                                  />
                                  <div
                                    className={classes.bar}
                                    style={{
                                      background: 'rgba(237, 114, 67, 1)',
                                      height: `${(d.women / 20) * 150}px`,
                                    }}
                                  />
                                  <div
                                    className={classes.bar}
                                    style={{
                                      background: 'rgba(154, 230, 231, 1)',
                                      height: `${(d.unknown / 20) * 150}px`,
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            <div className={classes.barChartLabelsRow}>
                              {[
                                '18-24',
                                '25-34',
                                '35-44',
                                '45-54',
                                '55-64',
                                '65+',
                              ].map((age) => (
                                <span className={classes.barLabel} key={age}>
                                  {age}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className={classes.barLegend}>
                          <span
                            className={classes.legendDot}
                            style={{ background: 'rgba(128, 128, 247, 1)' }}
                          />
                          <span className={classes.legendLabel}>Men</span>
                          <span
                            className={classes.legendDot}
                            style={{ background: 'rgba(237, 114, 67, 1)' }}
                          />
                          <span className={classes.legendLabel}>Women</span>
                          <span
                            className={classes.legendDot}
                            style={{ background: 'rgba(154, 230, 231, 1)' }}
                          />
                          <span className={classes.legendLabel}>Unknown</span>
                        </div>
                      </div>

                      {/* Location */}
                      <div className={classes.deliverySection}>
                        <span className={classes.deliverySectionTitle}>
                          Location
                        </span>
                        <div className={classes.locationChart}>
                          {[
                            { label: 'Tbilisi', pct: 35 },
                            { label: 'Imereti', pct: 25 },
                            { label: 'Adjara', pct: 20 },
                            { label: 'Samegrelo-Zemo Svaneti', pct: 10 },
                            { label: 'Kvemo Kartli', pct: 5 },
                            { label: 'Shida Kartli', pct: 5 },
                          ].map((d) => (
                            <div className={classes.locationRow} key={d.label}>
                              <div className={classes.locationBarWrap}>
                                <div
                                  className={classes.locationBar}
                                  style={{ width: `${d.pct}%` }}
                                />
                              </div>
                              <span className={classes.locationLabel}>
                                {d.label} {d.pct}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* About the advertiser */}
            <div className={classes.advertiserCard}>
              <button
                className={classes.advertiserHeader}
                onClick={() => setAdvertiserOpen((v) => !v)}
                type="button"
              >
                <span className={classes.advertiserTitle}>
                  About the advertiser
                </span>
                <ChevronIcon open={advertiserOpen} />
              </button>

              {advertiserOpen && (
                <>
                  <div className={classes.advertiserProfile}>
                    <img
                      alt={pageName}
                      className={classes.advertiserLogo}
                      src={pageLogo}
                    />
                    <div className={classes.advertiserDetails}>
                      <span className={classes.advertiserName}>{pageName}</span>
                      <div className={classes.advertiserPlatform}>
                        <div className={classes.advertiserRow}>
                          <FacebookIcon className={classes.socialIcon} />
                          <span className={classes.advertiserHandle}>
                            @{pageName.toLowerCase().replace(/\s/g, '.')}
                          </span>
                        </div>
                        <span className={classes.advertiserMeta}>
                          332.6K followers • Real Estate Title & Development
                        </span>
                      </div>
                      <div className={classes.advertiserPlatform}>
                        <div className={classes.advertiserRow}>
                          <InstagramIcon className={classes.socialIcon} />
                          <span className={classes.advertiserHandle}>
                            {'@'}
                            {pageName.toLowerCase().replace(/\s/g, '.')}
                            {'.development'}
                          </span>
                        </div>
                        <span className={classes.advertiserMeta}>
                          328.7K followers
                        </span>
                      </div>
                    </div>
                  </div>

                  {isPolitical && (
                    <div className={classes.fundingSources}>
                      <span className={classes.fundingTitle}>
                        Funding sources for ads about social issues, elections
                        or politics
                      </span>
                      <p className={classes.fundingSubtext}>
                        We require advertisers to verify the source of funds for
                        every ad about social issues, elections or politics. An
                        advertiser can declare multiple sources of funding.
                      </p>

                      <div className={classes.spendBox}>
                        <div className={classes.spendBoxHeader}>
                          <span className={classes.spendBoxTitle}>
                            Total amount spent $779,777
                          </span>
                          <span className={classes.spendBoxMeta}>
                            Aug 4, 2020 - Apr 5, 2026 in Georgia
                          </span>
                          <span className={classes.spendBoxDesc}>
                            The estimated total amount of money this advertiser
                            has spent on ads about social issues, elections or
                            politics.
                          </span>
                        </div>
                        <div className={classes.spendTable}>
                          <div className={classes.spendTableHeader}>
                            <span>Disclaimers</span>
                            <span>Spend per disclaimer</span>
                          </div>
                          {[
                            {
                              disclaimer: 'These ads ran without a disclaimer.',
                              spend: '≤$100',
                            },
                            { disclaimer: 'Georgian Dream', spend: '$510,475' },
                            {
                              disclaimer: 'ქართული ოცნება / Georgian Dream',
                              spend: '$175,583',
                            },
                            { disclaimer: 'Georgian Dream', spend: '$93,719' },
                          ].map((row) => (
                            <div
                              className={classes.spendTableRow}
                              key={row.spend + row.disclaimer}
                            >
                              <span className={classes.spendTableCell}>
                                {row.disclaimer}
                              </span>
                              <span className={classes.spendTableCellRight}>
                                {row.spend}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={classes.spendBox}>
                        <div className={classes.spendBoxHeader}>
                          <span className={classes.spendBoxTitle}>
                            Last week&apos;s spend $4,247
                          </span>
                          <span className={classes.spendBoxMeta}>
                            7 days • Mar 30 - Apr 5, 2026 in Georgia
                          </span>
                          <span className={classes.spendBoxDesc}>
                            The estimated amount of money this advertiser spent
                            during the last week on ads about social issues,
                            elections or politics.
                          </span>
                        </div>
                        <div className={classes.spendTable}>
                          <div className={classes.spendTableHeader}>
                            <span>Disclaimers</span>
                            <span>Spend per disclaimer</span>
                          </div>
                          <div className={classes.spendTableRow}>
                            <span className={classes.spendTableCell}>
                              Georgian Dream
                            </span>
                            <span className={classes.spendTableCellRight}>
                              $4,247
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={classes.moreInfo}>
                    <span className={classes.moreInfoTitle}>More info</span>
                    <p className={classes.moreInfoText}>
                      არჩი ლიდერი დეველოპერული კომპანიაა, რომელიც ბინის შეძენის
                      მსურველებს საუკეთესო არჩევანს სთავაზობს.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className={classes.adsDataSeparator} />
            <button className={classes.adsDataLink} type="button">
              About ads and data use
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
